/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useEffect, useState } from "react";
import { ListGroup, Image } from "react-bootstrap";
import { useTranslate } from "../../hooks/LanguageProvider";

interface Contact {
  id_user: number;
  nom_user: string;
  prenom_user: string;
  username_user: string;
  position: string;
  phone_number: string;
  email_user: string;
  img: string;
  avatar: string;
  status: string;
}

interface ContactProps {
  id_contact: number;
  activeContactId?: number;
  handleContactClick: (chatId: any) => void;
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
}

interface ChatSummary {
  id: number;
  name: string;
  avatar: string;
  status: string;
  lastMessage: string;
  lastSeen: string;
}

const chats: ChatSummary[] = [
  // Reserved for real conversation summaries when the backend exposes them.
];

const DEFAULT_USER_IMAGE = "asset/images/logo.png";

const AvatarImage: React.FC<{
  src: string;
  alt: string;
  fallback: React.ReactNode;
}> = ({ src, alt, fallback }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className="rounded-circle border border-light shadow-sm"
      style={{
        width: "42px",
        height: "42px",
        objectFit: "cover",
      }}
      onError={() => setHasError(true)}
    />
  );
};

const normalizeContactPayload = (payload: any): Contact | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return {
    id_user: Number(payload.id_user || 0),
    nom_user: String(payload.nom_user ?? payload.first_name ?? ""),
    prenom_user: String(payload.prenom_user ?? payload.last_name ?? ""),
    username_user: String(payload.username_user ?? payload.username ?? ""),
    position: String(payload.position ?? payload.user_type ?? ""),
    phone_number: String(payload.phone_number ?? payload.phone_user ?? payload.phone ?? ""),
    email_user: String(payload.email_user ?? payload.email ?? ""),
    img: String(payload.img ?? payload.avatar ?? DEFAULT_USER_IMAGE),
    avatar: String(payload.avatar ?? payload.img ?? DEFAULT_USER_IMAGE),
    status: String(payload.status ?? payload.chat_status ?? ""),
  };
};

const normalizeContactsPayload = (payload: unknown): Contact[] => {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((item) => normalizeContactPayload(item))
    .filter((item): item is Contact => Boolean(item?.id_user));
};

const ChatContacts: React.FC<ContactProps> = ({
  id_contact,
  activeContactId = 0,
  handleContactClick,
  onToggleSidebar,
  isSidebarOpen,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { translate } = useTranslate();
  const id_user = parseInt(localStorage.getItem("userid") ?? "0");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  );
  //const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [userLSender, setUser] = useState<Contact>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const resolveUserImageUrl = (image?: string) => {
    if (!image || image === DEFAULT_USER_IMAGE) {
      return "";
    }

    if (/^https?:\/\//i.test(image)) {
      return image;
    }

    const cleanImage = image
      .replace(/^(\.\.\/)+/, "")
      .replace(/^react\/public\//, "")
      .replace(/^public\//, "")
      .replace(/^\/+/, "");

    if (!cleanImage || cleanImage === DEFAULT_USER_IMAGE) {
      return "";
    }

    if (cleanImage.startsWith("asset/")) {
      return `/${cleanImage}`;
    }

    const baseUrl = (backendUrl ?? window.location.origin).replace(/\/$/, "");
    return `${baseUrl}/${cleanImage}`;
  };

  const getInitials = (contact?: Partial<Contact>) => {
    const firstName = contact?.nom_user?.trim()?.[0] ?? "";
    const lastName = contact?.prenom_user?.trim()?.[0] ?? "";
    const username = contact?.username_user?.trim()?.[0] ?? "";
    const email = contact?.email_user?.trim()?.[0] ?? "";
    return `${firstName}${lastName}`.toUpperCase() || username.toUpperCase() || email.toUpperCase() || "A";
  };

  const getContactDisplayName = (contact: Contact) => {
    const fullName = `${contact.nom_user || ""} ${contact.prenom_user || ""}`.trim();

    return (
      fullName ||
      contact.username_user ||
      contact.email_user ||
      `Utilisateur #${contact.id_user}`
    );
  };

  const getUserById = async (userId: number) => {
    const response = await fetch(`${backendUrl}/api/chats/user/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la recuperation du contact");
    }

    return normalizeContactPayload(await response.json());
  };

  const mergeContacts = (items: Contact[], extra?: Contact | null) => {
    if (!extra || items.some((item) => item.id_user === extra.id_user)) {
      return items;
    }

    return [extra, ...items];
  };

  const getContacts = async () => {
    try {
      void getUser();

      if (!id_user) {
        throw new Error("Utilisateur non connecté ou ID manquant.");
      }

      setLoading(true);

      const response = await fetch(
        `${backendUrl}/api/chats/agents/${id_user}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des agents");
      }

      const data = normalizeContactsPayload(await response.json());
      const activeContact =
        activeContactId && activeContactId !== id_user
          ? await getUserById(activeContactId).catch(() => null)
          : null;

      setContacts(mergeContacts(data, activeContact));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUser = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/chats/user/${id_user}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Error retrieving contacts");
      }

      const data = normalizeContactPayload(await response.json());
      if (!data) {
        return;
      }
      setUser(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getContacts();
  }, [activeContactId]);

  useEffect(() => {
    if (activeContactId) {
      setSelectedContactId(activeContactId);
      setSelectedChatId(null);
    }
  }, [activeContactId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  // Filter chat based on search
  const filteredChats = chats.filter(
    (chat) =>
      chat.name.toLowerCase().includes(searchTerm) ||
      chat.lastMessage.toLowerCase().includes(searchTerm)
  );

  // Filter contacts based on search

  const filteredContacts = contacts.filter(
    (contact) =>
      (contact.nom_user &&
        contact.nom_user.toLowerCase().includes(searchTerm)) ||
      (contact.prenom_user &&
        contact.prenom_user.toLowerCase().includes(searchTerm)) ||
      (contact.email_user &&
        contact.email_user.toLowerCase().includes(searchTerm)) ||
      (contact.position && contact.position.toLowerCase().includes(searchTerm))
  );

  const handleChatClick = (chatId: number) => {
    setSelectedChatId(chatId);
    setSelectedContactId(null);
  };

  // getColorByLetter.ts
  const colors = [
    "bg-primary",
    "bg-success",
    "bg-danger",
    "bg-warning",
    "bg-info",
    "bg-secondary",
    "bg-dark",
  ] as const;

  type BootstrapBgClass = (typeof colors)[number];

  function getColorByLetter(letter: string): BootstrapBgClass {
    const charCode = letter.toUpperCase().charCodeAt(0); // 'A' => 65
    const index = (charCode - 65) % colors.length;
    return colors[index];
  }

  const renderAvatar = (contact: Contact) => {
    const imageUrl = resolveUserImageUrl(contact.img || contact.avatar);
    const fallback = (
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${getColorByLetter(
          getInitials(contact)[0] || "A"
        )}`}
        style={{ width: "42px", height: "42px" }}
      >
        {getInitials(contact)}
      </div>
    );

    if (imageUrl) {
      return (
        <AvatarImage
          src={imageUrl}
          alt={`${contact.nom_user} ${contact.prenom_user}`}
          fallback={fallback}
        />
      );
    }

    return fallback;
  };

  return (
    <div
      className={`col app-chat-contacts app-sidebar ${isSidebarOpen ? "show" : ""
        }`}
      id="app-chat-contacts"
      style={{ background: "#fff", borderRight: "1px solid #e9ecef" }}
    >

      <button
        onClick={onToggleSidebar}
        className="position-absolute top-0 end-0 d-flex d-lg-none align-items-center justify-content-center"
        style={{
          background: 'transparent',
          border: '1px solid #ddd',
          boxShadow: 'none',
          width: '30px',
          height: '30px',
          borderRadius: '50%',
          margin:"3px 2px  0 0",
          zIndex: 1050
        }}
      >
        <i className="la la-times fs-5 text-dark"></i>
      </button>
      {/* <div
        id="app-chat-sidebar-left"
        style={{ background: "#fff", border: "1px solid #ddd" }}
      >
        <div
          className="sidebar-header h-px-75 px-5 border-bottom d-flex flex-column align-items-center text-center"
          style={{ margin: "15px 0", height: "120px !important" }}
        >
          <div
            className="avatar avatar-xl avatar-online chat-sidebar-avatar"
            onClick={onToggleSidebar}
          >
            <img
              src={`../asset/images/avatars/${userLSender?.id_user}.png`}
              alt="Avatar"
              className="rounded-circle"
              style={{ height: "41px" }}
            />
          </div>
          <h5 className="mt-2 mb-0">
            {userLSender?.nom_user + " " + userLSender?.prenom_user}
          </h5>
          <span className="mb-2">Admin</span>
          <i
            className="ti ti-x ti-lg cursor-pointer close-sidebar"
            onClick={onToggleSidebar}
          ></i>
        </div>

        <div className="sidebar-body px-6 pb-6" style={{ overflowY: "auto" }}>
          <div className="my-6">
            <label
              htmlFor="chat-sidebar-left-user-about"
              className="text-uppercase text-muted mb-1"
            >
              About
            </label>
            <textarea
              id="chat-sidebar-left-user-about"
              className="form-control chat-sidebar-left-user-about"
              rows={3}
              maxLength={120}
            >
              Hey there, we’re just writing to let you know that you’ve been
              subscribed to a repository on GitHub.
            </textarea>
          </div>
          <div className="my-6">
            <p className="text-uppercase text-muted mb-1">Status</p>
            <div className="d-grid gap-2 pt-2 text-heading ms-2">
              <div className="form-check form-check-success">
                <input
                  name="chat-user-status"
                  className="form-check-input"
                  type="radio"
                  value="active"
                  id="user-active"
                  defaultChecked
                />
                <label className="form-check-label" htmlFor="user-active">
                  Online
                </label>
              </div>
              <div className="form-check form-check-warning">
                <input
                  name="chat-user-status"
                  className="form-check-input"
                  type="radio"
                  value="away"
                  id="user-away"
                />
                <label className="form-check-label" htmlFor="user-away">
                  Away
                </label>
              </div>
              <div className="form-check form-check-danger">
                <input
                  name="chat-user-status"
                  className="form-check-input"
                  type="radio"
                  value="busy"
                  id="user-busy"
                />
                <label className="form-check-label" htmlFor="user-busy">
                  Do not Disturb
                </label>
              </div>
              <div className="form-check form-check-secondary">
                <input
                  name="chat-user-status"
                  className="form-check-input"
                  type="radio"
                  value="offline"
                  id="user-offline"
                />
                <label className="form-check-label" htmlFor="user-offline">
                  Offline
                </label>
              </div>
            </div>
          </div>
          <div className="my-6">
            <p className="text-uppercase text-muted mb-1">Settings</p>
            <ul className="list-unstyled d-grid gap-4 ms-2 pt-2 text-heading">
              <li className="d-flex justify-content-between align-items-center">
                <div>
                  <i className="ti ti-lock ti-md me-1"></i>
                  <span className="align-middle">Two-step Verification</span>
                </div>
                <div className="form-check form-switch mb-0 me-1">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    defaultChecked
                  />
                </div>
              </li>
              <li className="d-flex justify-content-between align-items-center">
                <div>
                  <i className="ti ti-bell ti-md me-1"></i>
                  <span className="align-middle">Notification</span>
                </div>
                <div className="form-check form-switch mb-0 me-1">
                  <input type="checkbox" className="form-check-input" />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div> */}



      <div className="sidebar-header px-4 border-bottom d-flex flex-column align-items-stretch justify-content-center gap-2">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Discussions</h5>
          <span className="badge bg-warning text-dark">
            {contacts.filter((item) => item.id_user !== id_user).length}
          </span>
        </div>
        <div className="d-flex align-items-center me-6 me-lg-0">
          <div className="flex-grow-1 input-group input-group-merge">
            <span className="input-group-text" id="basic-addon-search31">
              <i className="las la-search"></i>
            </span>
            <input
              type="text"
              className="form-control chat-search-input"
              placeholder="Rechercher un contact..."
              aria-label="Rechercher un contact"
              aria-describedby="basic-addon-search31"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <i className="ti ti-x ti-lg cursor-pointer position-absolute top-50 end-0 translate-middle d-lg-none d-block"></i>
      </div>

      <div className="sidebar-body" style={{ padding: "5px" }}>
        <ListGroup.Item as="li" style={{ border: "0px" }}>
          <h6 className="mb-0 text-muted">Conversations recentes</h6>
        </ListGroup.Item>

        <ListGroup
          as="ul"
          className="list-unstyled chat-contact-list py-2 mb-0"
          id="chat-list"
          style={{ height: "auto", maxHeight: "300px", overflowY: "auto" }}
        >
          {filteredChats.length === 0 ? (
            <ListGroup.Item as="li" className="chat-contact-list-item">
              <span className="text-muted mb-0 small">
                Aucune conversation recente
              </span>
            </ListGroup.Item>
          ) : (
            filteredChats.map((chat) => (
              <ListGroup.Item
                key={chat.id}
                as="span"
                onClick={() => {
                  handleChatClick(chat.id);
                  onToggleSidebar();
                }}
                className={`chat-contact-list-item ${selectedChatId === chat.id ? "selected-chat" : ""
                  }`}
                style={{
                  cursor: "pointer",
                  border:
                    selectedChatId === chat.id ? "2px solid #e84545" : "none",
                }}
              >
                <span className="d-flex align-items-center">
                  <div className={`flex-shrink-0 avatar avatar-${chat.status}`}>
                    {chat.avatar ? (
                      <Image
                        src={chat.avatar}
                        alt="Avatar"
                        className="rounded-circle"
                        style={{ height: "41px" }}
                      />
                    ) : (
                      <span className="avatar-initial rounded-circle bg-label-success">
                        { }
                      </span>
                    )}
                  </div>
                  <div className="chat-contact-info flex-grow-1 ms-4">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <h6 className="chat-contact-name fw-normal m-0">
                        {chat.name}
                      </h6>
                      <small className="">{chat.lastSeen}</small>
                    </div>
                    <small className="chat-contact-status text-truncate">
                      {chat.lastMessage.length > 10
                        ? `${chat.lastMessage.substring(0, 10)}...`
                        : chat.lastMessage}
                    </small>
                  </div>
                </span>
              </ListGroup.Item>
            ))
          )}
        </ListGroup>

        {/* List of contacts */}
        <ListGroup.Item as="li" style={{ border: "0px" }}>
          <h6 className="mb-0 text-muted">Contacts disponibles</h6>
        </ListGroup.Item>
        <ListGroup
          as="ul"
          className="list-unstyled chat-contact-list mb-0 py-2"
          id="contact-list"
          style={{ height: "400px", overflowY: "auto" }}
        >
          {loading ? (
            <ListGroup.Item as="li" className="chat-contact-list-item">
              <span className="text-muted mb-0 small">Chargement...</span>
            </ListGroup.Item>
          ) : error ? (
            <ListGroup.Item as="li" className="chat-contact-list-item">
              <span className="text-danger mb-0 small">{error}</span>
            </ListGroup.Item>
          ) : filteredContacts.length === 0 ? (
            <ListGroup.Item as="li" className="chat-contact-list-item">
              <span className="text-muted mb-0 small">Aucun contact trouve</span>
            </ListGroup.Item>
          ) : (
            filteredContacts
              .filter((contact) => contact.id_user !== id_user)
              .map((contact) => (
                <ListGroup.Item
                  key={contact.id_user}
                  onClick={() => {
                    setSelectedContactId(contact.id_user);
                    setSelectedChatId(null);
                    handleContactClick(contact);
                    onToggleSidebar();
                  }}
                  className={`chat-contact-list-item ${selectedContactId === contact.id_user ? "selected-chat" : ""
                    }`}
                  style={{
                    cursor: "pointer",
                    background:
                      selectedContactId === contact.id_user
                        ? "#fff7d6"
                        : "transparent",
                    border:
                      selectedContactId === contact.id_user
                        ? "1px solid #facc15"
                        : "1px solid transparent",
                    boxShadow:
                      selectedContactId === contact.id_user
                        ? "0 8px 18px rgba(245, 158, 11, 0.16)"
                        : "none",
                  }}
                  as="span"
                >
                  <span className="d-flex align-items-center">
                    <div className={`flex-shrink-0 avatar ${contact.status}`}>
                      {renderAvatar(contact)}
                    </div>
                    <div className="chat-contact-info flex-grow-1 ms-4">
                      <h6 className="chat-contact-name m-0 fw-normal">
                        {getContactDisplayName(contact)}
                      </h6>
                      <small className="chat-contact-status text-truncate">
                        {contact.position || contact.email_user || "Contact AIDA"}
                      </small>
                    </div>
                  </span>
                </ListGroup.Item>
              ))
          )}
        </ListGroup>
      </div>
    </div>
  );
};

export default ChatContacts;
